import { useEffect, useState } from "react";
import { ApiError, api, type PushDeviceOut, type PushTestOut } from "@/lib/api";
import { getDeviceId } from "@/lib/push";
import { useToast } from "@/ui/Toast";

/**
 * Lista "Mis equipos": los dispositivos (suscripciones Web Push) del usuario.
 * Deja probar o quitar cualquiera y marca cuál es ESTE equipo (por device_id)
 * y cuáles están "sin confirmar" (rancios = probables zombies). Nace del
 * diagnóstico 2026-06-12: el push sí sobrevive al logout, pero hacían falta
 * visibilidad y control por dispositivo.
 *
 * Qué hacen «Probar» y «Quitar» (y por qué un equipo quitado puede volver solo)
 * lo explica la Ayuda de Mi perfil (`components/ayuda/vistas/perfil.tsx`).
 */
export default function MisEquipos({ reloadKey }: { reloadKey: number }) {
  const toast = useToast();
  const [equipos, setEquipos] = useState<PushDeviceOut[] | null>(null);
  const [ocupado, setOcupado] = useState<number | null>(null);
  const esteId = getDeviceId();

  async function cargar() {
    try {
      setEquipos(await api.get<PushDeviceOut[]>("/api/push/devices"));
    } catch {
      setEquipos([]);
    }
  }

  useEffect(() => {
    void cargar();
  }, [reloadKey]);

  async function probar(id: number) {
    setOcupado(id);
    try {
      const r = await api.post<PushTestOut>(`/api/push/devices/${id}/test`, {});
      if (r.enviados > 0) {
        toast.success(
          "Prueba enviada a ese equipo",
          "Revisa que la notificación haya aparecido ahí.",
        );
      } else {
        toast.error(
          "Ese equipo ya no recibe",
          "Se dio de baja porque el navegador lo rechazó.",
        );
      }
      await cargar();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude enviar la prueba", msg);
    } finally {
      setOcupado(null);
    }
  }

  async function quitar(id: number) {
    setOcupado(id);
    try {
      await api.del(`/api/push/devices/${id}`);
      toast.success("Equipo dado de baja", "Dejará de recibir notificaciones.");
      await cargar();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude quitar el equipo", msg);
    } finally {
      setOcupado(null);
    }
  }

  if (equipos === null || equipos.length === 0) return null;

  return (
    <div className="border-t border-ink-700 pt-3 space-y-2">
      <div>
        <h3 className="text-xs font-medium text-zinc-300">Mis equipos</h3>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          Dispositivos dados de alta para recibir avisos. Prueba o quita
          cualquiera; los{" "}
          <span className="text-amber-300">sin confirmar</span> suelen ser
          navegadores que ya no escuchan.
        </p>
      </div>
      <ul className="divide-y divide-ink-800">
        {equipos.map((e) => {
          const esEste = e.device_id != null && e.device_id === esteId;
          const ok = e.estado === "ok";
          return (
            <li
              key={e.id}
              className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm text-zinc-200">{e.etiqueta}</span>
                  {esEste && (
                    <span className="px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide bg-accent-500/10 text-accent-400 border-accent-500/30">
                      este equipo
                    </span>
                  )}
                  <span
                    className={`px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ${
                      ok
                        ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                        : "bg-amber-500/10 text-amber-300 border-amber-500/30"
                    }`}
                  >
                    {ok ? "confirmado" : "sin confirmar"}
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  {e.dias_inactivo === 0
                    ? "Confirmado hoy"
                    : `Última confirmación hace ${e.dias_inactivo} día${
                        e.dias_inactivo === 1 ? "" : "s"
                      }`}
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => void probar(e.id)}
                  disabled={ocupado === e.id}
                  className="btn-ghost text-xs disabled:opacity-50"
                >
                  Probar
                </button>
                <button
                  onClick={() => void quitar(e.id)}
                  disabled={ocupado === e.id}
                  className="btn-ghost text-xs text-red-300 hover:text-red-200 disabled:opacity-50"
                >
                  Quitar
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
